import type { CameraState } from '@mighty-map/map-engine';

/** Previous / next extent: the views the map has settled on, and where we are among them. */
export interface CameraHistory {
  readonly entries: readonly CameraState[];
  /** Index of the current view in `entries`; -1 when empty. */
  readonly index: number;
}

export const EMPTY_HISTORY: CameraHistory = { entries: [], index: -1 };
export const HISTORY_LIMIT = 50;

/** Same view to within about a pixel: settling after a jump to a stored view must not add a new entry. */
export function sameView(a: CameraState, b: CameraState): boolean {
  const pixelDeg = 360 / (512 * 2 ** Math.max(a.zoom, b.zoom));
  return (
    Math.abs(a.zoom - b.zoom) < 0.01 &&
    Math.abs(a.center[0] - b.center[0]) < pixelDeg &&
    Math.abs(a.center[1] - b.center[1]) < pixelDeg &&
    Math.abs(a.bearing - b.bearing) < 0.1 &&
    Math.abs(a.pitch - b.pitch) < 0.1
  );
}

/** Record a settled view. A new view after going back drops the views ahead of it (browser-history rules). */
export function recordView(history: CameraHistory, view: CameraState, limit = HISTORY_LIMIT): CameraHistory {
  const current = history.entries[history.index];
  if (current && sameView(current, view)) return history;
  const entries = [...history.entries.slice(0, history.index + 1), view].slice(-limit);
  return { entries, index: entries.length - 1 };
}

export const canGoBack = (h: CameraHistory): boolean => h.index > 0;
export const canGoForward = (h: CameraHistory): boolean => h.index >= 0 && h.index < h.entries.length - 1;

/** Step back or forward; null when there is nowhere to go. */
export function stepView(h: CameraHistory, direction: -1 | 1): { history: CameraHistory; view: CameraState } | null {
  const index = h.index + direction;
  const view = h.entries[index];
  if (index < 0 || !view) return null;
  return { history: { entries: h.entries, index }, view };
}
